/**
 * Der Chip „Morgenreport“ im Chat. (19.10.2026, Paket 1, shared/morgenreport.ts, server/morgenreport.ts)
 *
 * Martin, 19.10.: nicht mehr als große Karte, sondern als kleiner Knopf mit Punkt, solange der Report ungelesen ist. Tippen öffnet
 * die In-App-Seite #morgenreport (Martin bleibt in der App) und vermerkt beim Server „gelesen“ (geschlossen_ms, je Tag, alle Geräte).
 * Danach verschwindet der Chip (Menüpunkt „Morgenreport“ bleibt der Zugang); das „×“ blendet ihn ohne Öffnen aus — nie wieder eine Karte.
 *
 * ⚠️ Der Chip liest nie vor, öffnet nie das Mikro und sendet nichts. Kein Chat-Beitrag und kein Push: er wird aus
 *    GET /api/morgenreport/karte gezeichnet. Alle Texte nur über textContent. Aussehen: style.css (.morgen-chip).
 */
import { GELESEN_EREIGNIS, GEHOERT_PFAD, SCHLIESSEN_PFAD, KARTE_PFAD, SEITE_HASH, leseKartenStand, tagAnzeige, type KartenStand } from "../../shared/morgenreport.js";

export const KARTEN_KLASSE = "morgen-chip";
/** Die vorhandene Vorlese-Schiene (app.ts setzt den Knopf in den Platz; Tippen liest, wie an jeder Antwort). */
export const VORLESE_EREIGNIS = "voice-v8:vorlese-knopf";
/** Pflichtkopf der schreibenden Wege (server/morgenreport-routen.ts). */
export const AKTION_KOPF = "X-Vespera-Aktion";
export const AKTION_WERT = "morgenreport";
/** So oft wird nachgesehen, ob ein neuer Report da ist. */
export const NACHSEHEN_MS = 10 * 60_000;

export type KartenSender = (tag: string) => Promise<boolean> | boolean;

/** Schickt „Tag“ an einen schreibenden Weg; wirft nie. */
const sendeTag = (pfad: string): KartenSender => async (tag) => {
  try {
    const r = await fetch(pfad, {
      method: "POST",
      credentials: "include",
      headers: { [AKTION_KOPF]: AKTION_WERT, "Content-Type": "application/json" },
      body: JSON.stringify({ tag }),
    });
    return r.ok;
  } catch {
    return false;
  }
};
/** Vermerkt beim Server, dass Martin „Vorlesen“ getippt hat. */
export const sendeGehoert: KartenSender = sendeTag(GEHOERT_PFAD);
/** Vermerkt beim Server, dass Martin den Report geöffnet oder als gelesen markiert hat (gilt für alle Geräte, für diesen Tag). */
export const sendeGeschlossen: KartenSender = sendeTag(SCHLIESSEN_PFAD);

/**
 * Der Chip (noch nicht eingehängt): Verweis „Morgenreport“ (+ Punkt solange ungelesen) und daneben ein „×“ zum Ausblenden. Wirft nie.
 * `beiTipp` läuft beim Antippen des Verweises (Punkt weg; die Seite öffnet der Verweis selbst), `beiAusblenden` nach dem „×“.
 * Beide Wege vermerken beim Server „gelesen“ (geschlossen_ms): der Chip erscheint für diesen Report nirgends mehr.
 */
export function baueMorgenKarte(doc: Document, stand: KartenStand, sendeGelesen: KartenSender = sendeGeschlossen, beiTipp: () => void = () => undefined, beiAusblenden: () => void = () => undefined): HTMLElement {
  const chip = doc.createElement("span");
  chip.className = KARTEN_KLASSE;
  chip.dataset.tag = stand.tag;
  const link = doc.createElement("a");
  link.className = "morgen-chip-name";
  link.href = `#${SEITE_HASH}`; // In-App-Seite über den Seiten-Router (hashchange), kein Neuladen
  link.textContent = "Morgenreport";
  chip.appendChild(link);
  const meldung = (gelesen: boolean): string => `Morgenreport ${tagAnzeige(stand.tag)}, ${gelesen ? "gelesen" : "ungelesen"}`;
  const zeichnePunkt = (gelesen: boolean): void => {
    link.querySelector(".morgen-punkt")?.remove();
    link.setAttribute("aria-label", meldung(gelesen));
    if (gelesen) return;
    const punkt = doc.createElement("span");
    punkt.className = "morgen-punkt";
    punkt.setAttribute("aria-hidden", "true");
    link.appendChild(punkt);
  };
  zeichnePunkt(stand.gelesen);
  let vermerkt = stand.gelesen;
  const vermerke = (): void => {
    if (vermerkt) return;
    vermerkt = true;
    void Promise.resolve(sendeGelesen(stand.tag)).catch(() => undefined);
  };
  link.addEventListener("click", (ev) => {
    if (!ev.isTrusted || vermerkt) return; // nur echte Tipps zählen (kein Skript, kein Modell)
    zeichnePunkt(true);
    vermerke();
    beiTipp();
  });
  const aus = doc.createElement("button");
  aus.type = "button";
  aus.className = "morgen-chip-aus";
  aus.setAttribute("aria-label", "Morgenreport ausblenden");
  aus.textContent = "\u00d7";
  aus.addEventListener("click", (ev) => {
    if (!ev.isTrusted) return;
    vermerke();
    chip.remove();
    beiAusblenden();
  });
  chip.appendChild(aus);
  return chip;
}

/**
 * Holt den Stand. `null` = der Server sagt ausdrücklich „keine Karte“ (aus, zu alt, leer); `undefined` = Abruf oder Antwort
 * unbrauchbar (Netz, 5xx, Neustart) — dann bleibt eine stehende Karte stehen. (19.10.2026: vorher entfernte jeder Fehlabruf die Karte.)
 */
export async function holeKartenStand(): Promise<KartenStand | null | undefined> {
  try {
    const r = await fetch(KARTE_PFAD, { credentials: "include", cache: "no-store" });
    if (!r.ok) return undefined;
    const j = (await r.json()) as { karte?: unknown };
    if (j.karte === null) return null;
    return leseKartenStand(j.karte) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Hält den Chip am Ende des Verlaufs (idempotent, ein Tag = ein Chip). Er verschwindet nur, wenn der Server ausdrücklich keinen
 * Report meldet. Wird der Verlauf geleert und neu gezeichnet, hängt er sich sofort wieder ein. Ein Fehlabruf ändert nichts.
 */
export function baueMorgenAnzeige(wurzel: HTMLElement, hole: () => Promise<KartenStand | null | undefined> = holeKartenStand): { aktualisiere(): Promise<void> } {
  const doc = wurzel.ownerDocument;
  let chip: HTMLElement | null = null;
  let aktuell: KartenStand | null = null;
  const MO = doc.defaultView?.MutationObserver;
  if (MO) {
    // Der Chip steht immer am Ende: auch eine neu einsortierte Nachricht (Zeitstempel) schiebt ihn nicht nach oben.
    new MO(() => {
      if (chip && wurzel.lastElementChild !== chip) wurzel.appendChild(chip);
    }).observe(wurzel, { childList: true });
  }
  const api = {
    async aktualisiere() {
      const stand = await hole();
      if (stand === undefined) return; // Fehlabruf: nichts ändern
      // Nach dem Öffnen der Seite (gelesen, auch von einem anderen Gerät) oder dem „×“ gibt es keinen Chip mehr; er war nur der Hinweis „ungelesen“.
      if (!stand || stand.gelesen) {
        chip?.remove();
        chip = null;
        aktuell = null;
        return;
      }
      // Neuer Tag: neu zeichnen; sonst bleibt der Chip, wie er ist.
      if (chip?.dataset.tag !== stand.tag) {
        chip?.remove();
        chip = baueMorgenKarte(doc, stand, sendeGeschlossen, () => undefined, () => {
          chip = null;
          aktuell = null;
        });
        aktuell = stand;
      }
      if (wurzel.lastElementChild !== chip) wurzel.appendChild(chip);
    },
  };
  // Die Seite hat „gelesen“ gemeldet: sofort nachsehen, damit der Punkt verschwindet.
  doc.defaultView?.addEventListener(GELESEN_EREIGNIS, () => void api.aktualisiere());
  return api;
}
